import { useState } from "react";
import { Link } from "@/lib/router";
import { createClient } from "@/lib/supabase/client";
import { authErrorMessage } from "@/lib/auth/messages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * 비밀번호 재설정 메일 요청. 가입된 이메일인지 여부는 알려주지 않는다(계정 존재 여부를
 * 외부에서 알아내는 것을 막기 위해 결과가 어떻든 같은 안내를 보여준다). 메일의 링크는
 * Supabase "Reset password" 템플릿이 /auth/confirm?type=recovery로 보낸다.
 */
export function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const { error } = await createClient().auth.resetPasswordForEmail(email, {
      // 기본 메일 템플릿은 검증 후 이 주소로 ?code=를 붙여 보낸다(Redirect URLs에 등록 필요)
      redirectTo: `${window.location.origin}/auth/confirm?next=/auth/update-password`,
    });
    setIsLoading(false);

    // 너무 자주 요청한 경우 등 실제 실패만 알리고, 미가입 이메일은 성공처럼 보인다.
    if (error && (error.code === "over_email_send_rate_limit" || error.status === 429)) {
      setError(authErrorMessage(error));
      return;
    }
    setSent(true);
  }

  return (
    <Card className="w-full max-w-sm border-0 bg-transparent shadow-none ring-0">
      <CardHeader className="items-center text-center">
        <CardTitle className="text-2xl">비밀번호 찾기</CardTitle>
        <CardDescription>가입한 이메일로 재설정 링크를 보내드려요</CardDescription>
      </CardHeader>
      <CardContent>
        {sent ? (
          <div className="flex flex-col gap-4 text-center text-sm text-muted-foreground" role="status">
            <p>가입된 이메일이라면 재설정 링크를 보냈어요. 메일함(스팸함 포함)을 확인해주세요.</p>
            <Link href="/auth/login" className="text-foreground underline underline-offset-4">
              로그인 화면으로
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">이메일</Label>
              <Input
                className="h-11"
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={isLoading} className="h-11 w-full rounded-full">
              {isLoading ? "보내는 중..." : "재설정 링크 보내기"}
            </Button>
            <Link
              href="/auth/login"
              className="text-center text-sm text-muted-foreground underline underline-offset-4"
            >
              로그인으로 돌아가기
            </Link>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
